import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import store, { IAppState } from "../Store";
import Datastore from "../../communication/Datastore";
import { REHYDRATE } from "redux-persist";
import Dashboard, {
    DashboardActivity,
    DashboardContact,
} from "../../types/core/Dashboard";

// Define the type for our state store entry
type DashboardState = {
    dashboard: Dashboard | null;
    dashboardBanner: string | null;
    activities: DashboardActivity[] | null;
    contacts: DashboardContact[] | null;
    status: string;
    bannerStatus: string;
    activityStatus: string;
    contactStatus: string;
    error: string;
};

// Define the initial state for our state store entry
const initialState: DashboardState = {
    dashboard: null,
    dashboardBanner: null,
    activities: null,
    contacts: null,
    status: "idle",
    bannerStatus: "idle",
    activityStatus: "idle",
    contactStatus: "idle",
    error: null,
};

// Create a slice to hold all our reducers
const dashboardSlice = createSlice({
    name: "dashboard",
    initialState: initialState,
    reducers: {},
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;
                return {
                    dashboard: rawData?.payload?.dashboard?.dashboard,
                    dashboardBanner:
                        rawData?.payload?.dashboard?.dashboardBanner,
                    activities: rawData?.payload?.dashboard?.activities,
                    contacts: rawData?.payload?.dashboard?.contacts,
                    status: !rawData?.payload?.dashboard?.dashboard
                        ? "idle"
                        : rawData?.payload?.dashboard?.status,
                    bannerStatus: !rawData?.payload?.dashboard?.dashboardBanner
                        ? "idle"
                        : rawData?.payload?.dashboard?.bannerStatus,
                    activityStatus: !rawData?.payload?.dashboard?.activities
                        ? "idle"
                        : rawData?.payload?.dashboard?.activityStatus,
                    contactStatus: !rawData?.payload?.dashboard?.contacts
                        ? "idle"
                        : rawData?.payload?.dashboard?.contactStatus,
                    error: null,
                };
            })
            .addCase(fetchDashboard.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(fetchDashboard.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.dashboard = action.payload;
            })
            .addCase(fetchDashboard.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchDashboardBanner.pending, (state, action) => {
                state.bannerStatus = "loading";
            })
            .addCase(fetchDashboardBanner.fulfilled, (state, action) => {
                state.bannerStatus = "succeeded";
                // Add any fetched posts to the array
                state.dashboardBanner = action.payload;
            })
            .addCase(fetchDashboardBanner.rejected, (state, action) => {
                state.bannerStatus = "failed";
                state.error = action.error.message;
            })
            .addCase(saveDashboard.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(saveDashboard.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.bannerStatus = "idle";
                // Add any fetched posts to the array
                state.dashboard = action.payload;
            })
            .addCase(saveDashboard.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchDashboardActivities.pending, (state, action) => {
                state.activityStatus = "loading";
            })
            .addCase(fetchDashboardActivities.fulfilled, (state, action) => {
                state.activityStatus = "succeeded";
                state.activities = action.payload;
            })
            .addCase(fetchDashboardActivities.rejected, (state, action) => {
                state.activityStatus = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchDashboardContacts.pending, (state, action) => {
                state.contactStatus = "loading";
            })
            .addCase(fetchDashboardContacts.fulfilled, (state, action) => {
                state.contactStatus = "succeeded";
                state.contacts = action.payload;
            })
            .addCase(fetchDashboardContacts.rejected, (state, action) => {
                state.contactStatus = "failed";
                state.error = action.error.message;
            });
    },
});

export const initialiseDashboardDetails = createAsyncThunk(
    "dashboard/initialiseDashboardDetails",
    async (_, thunkAPI) => {
        await thunkAPI.dispatch(fetchDashboard());
        await thunkAPI.dispatch(fetchDashboardBanner());
    },
);

// This is a thunk, they are used to load the state
const fetchDashboard = createAsyncThunk(
    "dashboard/fetchDashboard",
    () => Datastore.getType<Dashboard>("api/Dashboard"),
    {
        condition: (_, api) =>
            store.getState().db.dashboard.status === "idle",
    },
);
const fetchDashboardBanner = createAsyncThunk(
    "dashboard/fetchDashboardBanner",
    () => {
        const state = store.getState().db;
        return Datastore.get(
            `api/SystemIO/MP/WidgetFile/${state.dashboard.dashboard.banner.token}`,
        ) as Promise<string>;
    },
    {
        condition: (_, api) => {
            const state = store.getState().db;
            return (
                state.dashboard.bannerStatus === "idle" &&
                !!state.dashboard.dashboard?.banner?.enabled
            );
        },
    },
);
export const getDashboardUpload = createAsyncThunk(
    "dashboard/getDashboardUpload",
    async (payload: string) =>
        Datastore.get(
            `api/SystemIO/MP/WidgetFile/${payload}`,
        ) as Promise<string>,
    {
        condition: (_, api) =>
            store.getState().db.dashboard.status === "succeeded",
    },
);
export const saveDashboardUpload = createAsyncThunk(
    "dashboard/saveDashboardUpload",
    async (payload: { token: string; data: string }) =>
        Datastore.post(
            `api/SystemIO/MP/WidgetFile/${payload.token}`,
            payload.data,
        ),
    {
        condition: (_, api) =>
            store.getState().db.dashboard.status === "succeeded",
    },
);
export const removeDashboardUpload = createAsyncThunk(
    "dashboard/removeDashboardUpload",
    async (payload: string) =>
        Datastore.delete(`api/SystemIO/MP/WidgetFile/${payload}`),
    {
        condition: (_, api) =>
            store.getState().db.dashboard.status === "succeeded",
    },
);
export const fetchDashboardContacts = createAsyncThunk(
    "dashboard/fetchDashboardContacts",
    async () => Datastore.getType<DashboardContact[]>(`api/Dashboard/Contacts`),
    {
        condition: (_, api) =>
            store.getState().db.dashboard.status === "succeeded",
    },
);
export const fetchDashboardActivities = createAsyncThunk(
    "dashboard/fetchDashboardActivities",
    async () =>
        Datastore.getType<DashboardActivity[]>(`api/Dashboard/Activities`),
    {
        condition: (_, api) =>
            store.getState().db.dashboard.status === "succeeded",
    },
);
export const saveDashboard = createAsyncThunk(
    "dashboard/saveDashboard",
    async (payload: Dashboard) => {
        if (payload.id) {
            return Datastore.putType<Dashboard>("api/Dashboard", payload);
        } else {
            return Datastore.postType<Dashboard>("api/Dashboard", payload);
        }
    },
    {
        condition: (_, api) =>
            store.getState().db.dashboard.status === "succeeded",
    },
);

// We have to export the reducer as this is added to the state store
export default dashboardSlice.reducer;
